<template>
  <v-flex xs12 class="mt-5">
    <h3 class="primary--text pb-2">Single Sign-On</h3>
    <p>Configure how users sign in to this account. Settings are stored on the account OIDC feature.</p>

    <v-tabs v-model="ssoTab" slider-color="primary" class="mt-2">
      <v-tab v-for="tab in ssoTabs" :key="tab.id" :disabled="tab.disabled">
        {{ tab.title }}
      </v-tab>
      <v-tab-item v-for="tab in ssoTabs" :key="tab.id + '-content'">
        <v-container fluid class="pa-0 pt-4">
          <div v-if="tab.id === 'oidc'">
            <v-switch
              v-model="enabled"
              label="Enable OpenID Connect"
              color="primary"
              class="mt-0"
              @change="emitSso"
            ></v-switch>
            <p class="caption grey--text mb-3">
              SSO is offered on the login page when this is enabled and issuer, client ID, and client secret are set
              (or supplied via server environment).
            </p>

            <v-text-field
              v-model="issuer"
              box
              label="Issuer URL"
              hint="OIDC discovery issuer, e.g. https://auth.example.com/application/o/caseblocks"
              persistent-hint
              class="mb-3"
              @input="emitSso"
            ></v-text-field>
            <v-text-field
              v-model="clientId"
              box
              label="Client ID"
              persistent-hint
              class="mb-3"
              @input="emitSso"
            ></v-text-field>
            <v-text-field
              v-model="clientSecret"
              box
              :type="showSecret ? 'text' : 'password'"
              label="Client secret"
              :placeholder="clientSecretConfigured ? '•••••••• (stored — enter a new secret to replace)' : ''"
              :hint="clientSecretConfigured ? 'Leave blank to keep the stored secret.' : 'Required for SSO until a secret is stored.'"
              persistent-hint
              append-icon="visibility"
              class="mb-3"
              @click:append="showSecret = !showSecret"
              @input="emitSso"
            ></v-text-field>
            <v-text-field
              v-model="buttonLabel"
              box
              label="Sign-in button label"
              hint="Shown on the login page. Default is Sign in with SSO."
              persistent-hint
              class="mb-3"
              @input="emitSso"
            ></v-text-field>
            <v-text-field
              v-model="scopes"
              box
              label="Scopes"
              hint="Space-separated. Default is openid email profile."
              persistent-hint
              class="mb-3"
              @input="emitSso"
            ></v-text-field>
            <v-text-field
              :value="currentRedirectUri"
              box
              readonly
              label="Redirect URI"
              hint="Register this URL with the identity provider. It is this Caseblocks host."
              persistent-hint
              class="mb-3"
            ></v-text-field>
            <v-autocomplete
              v-model="defaultTeam"
              :items="teams"
              item-text="displayName"
              item-value="displayName"
              box
              clearable
              label="Default team"
              hint="Team for users created on first sign-in."
              persistent-hint
              class="mb-3"
              @change="emitSso"
            ></v-autocomplete>
            <v-switch
              v-model="autoCreateUsers"
              label="Create users on first sign-in"
              color="primary"
              @change="emitSso"
            ></v-switch>
            <v-switch
              v-model="disablePasswordLogin"
              label="Disable password login"
              color="primary"
              @change="emitSso"
            ></v-switch>
          </div>
          <div v-else class="grey--text">
            <p class="mb-0">{{ tab.title }} configuration is not available yet.</p>
          </div>
        </v-container>
      </v-tab-item>
    </v-tabs>
  </v-flex>
</template>

<script>
import { mapState } from "vuex"

export default {
  name: "account-config-sso",
  props: ["account"],
  data() {
    return {
      syncing: false,
      ssoTab: 0,
      ssoTabs: [
        { id: "oidc", title: "OIDC", disabled: false },
        { id: "okta", title: "Okta", disabled: true },
        { id: "azure-ad", title: "Azure AD", disabled: true },
        { id: "saml", title: "SAML", disabled: true }
      ],
      enabled: false,
      issuer: "",
      clientId: "",
      clientSecret: "",
      clientSecretConfigured: false,
      buttonLabel: "Sign in with SSO",
      scopes: "openid email profile",
      defaultTeam: "",
      autoCreateUsers: false,
      disablePasswordLogin: false,
      showSecret: false
    }
  },
  computed: {
    ...mapState({
      teams: function(state) {
        return state.teams && state.teams.all ? state.teams.all : []
      }
    }),
    currentRedirectUri() {
      return this.oidcRedirectUriFromLocation()
    }
  },
  watch: {
    account: {
      handler(newAccount) {
        this.hydrateFromAccount(newAccount)
      },
      immediate: true
    }
  },
  methods: {
    oidcRedirectUriFromLocation() {
      if (typeof window === "undefined" || !window.location || !window.location.origin) {
        return ""
      }
      return String(window.location.origin).replace(/\/+$/, "") + "/sso/oidc/callback"
    },
    hydrateFromAccount(account) {
      this.syncing = true
      const sso = account && account.oidcSso ? account.oidcSso : {}
      this.enabled = !!sso.enabled
      this.issuer = sso.issuer || ""
      this.clientId = sso.clientId || ""
      this.clientSecret = ""
      this.clientSecretConfigured = !!sso.clientSecretConfigured
      this.buttonLabel = sso.buttonLabel || "Sign in with SSO"
      this.scopes = sso.scopes || "openid email profile"
      this.defaultTeam = sso.defaultTeam || ""
      this.autoCreateUsers = !!sso.autoCreateUsers
      this.disablePasswordLogin = !!sso.disablePasswordLogin
      this.$nextTick(() => {
        this.syncing = false
      })
    },
    buildPayload() {
      const payload = {
        enabled: this.enabled,
        issuer: this.issuer,
        clientId: this.clientId,
        buttonLabel: this.buttonLabel,
        scopes: this.scopes,
        redirectUri: this.currentRedirectUri,
        autoCreateUsers: this.autoCreateUsers,
        defaultTeam: this.defaultTeam || "",
        disablePasswordLogin: this.disablePasswordLogin
      }
      if (this.clientSecret && String(this.clientSecret).trim()) {
        payload.clientSecret = String(this.clientSecret).trim()
      }
      return payload
    },
    emitSso() {
      if (this.syncing) {
        return
      }
      this.$emit("update-account-data", { key: "changedOidcSso", value: this.buildPayload() })
    }
  }
}
</script>
